/**
 * V1.9.6 — Energy Score dialog (Studio Tier preview).
 *
 * Visualises the back-of-the-napkin envelope kWh/year + a 0..100
 * efficiency score for the building. The score is a single big chip;
 * below it sit the heat / cool breakdown and the climate region.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Zap, Flame, Snowflake, Wind, Sun, RefreshCw } from "lucide-react";
import { getEnergyScore } from "../lib/api";

const RATING_COLOR = {
  "A+": "#22c55e",
  A:    "#22c55e",
  B:    "#84cc16",
  C:    "#facc15",
  D:    "#fb923c",
  F:    "#ef4444",
  "?":  "#52525b",
};

function fmtKwh(n) {
  if (!n) return "0";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return Math.round(n).toLocaleString();
}

export default function EnergyScoreDialog({ open, onClose, designId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runScan = async () => {
    if (!designId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await getEnergyScore(designId);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not run energy estimate.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, designId]);

  const rating = data?.rating || "?";
  const ratingColor = RATING_COLOR[rating] || RATING_COLOR["?"];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-2xl bg-zinc-950 border-zinc-800 text-zinc-100"
        data-testid="energy-score-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-mono text-emerald-400 flex items-center gap-2">
            <Zap size={18} />
            // ENERGY SCORE
          </DialogTitle>
        </DialogHeader>

        {loading && !data && (
          <div className="py-10 text-center text-zinc-500 font-mono text-xs">
            // estimating envelope load against climate degree-days...
          </div>
        )}
        {error && (
          <div className="py-4 text-red-400 font-mono text-xs">// ERROR: {error}</div>
        )}

        {data && (
          <div data-testid="es-results" className="space-y-4">
            {/* Big rating chip */}
            <div className="flex items-center gap-6 border-b border-zinc-800 pb-4">
              <div
                data-testid="es-rating-chip"
                className="flex-shrink-0 flex items-center justify-center font-mono font-bold text-5xl w-24 h-24 rounded"
                style={{ background: `${ratingColor}22`, color: ratingColor, border: `1px solid ${ratingColor}55` }}
              >
                {rating}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                  efficiency score
                </div>
                <div className="text-4xl font-mono text-zinc-100" data-testid="es-score-number">
                  {data.score}
                  <span className="text-zinc-500 text-lg"> / 100</span>
                </div>
                <div className="text-xs text-zinc-400 mt-2">
                  Estimated annual envelope load:{" "}
                  <span className="text-emerald-400 font-mono" data-testid="es-annual-kwh">
                    {fmtKwh(data.annual_kwh)} kWh/yr
                  </span>
                  {" · "}
                  <span className="font-mono text-zinc-300">{data.kwh_per_m2} kWh/m²</span>
                </div>
              </div>
            </div>

            {/* Heat / Cool / Walls / Windows / Roof / Solar — 6 chips. */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              <BreakdownChip
                icon={<Flame size={14} />}
                color="#fb923c"
                label="Heating"
                value={`${fmtKwh(data.breakdown.heating_kwh)} kWh`}
                testid="es-breakdown-heating"
              />
              <BreakdownChip
                icon={<Snowflake size={14} />}
                color="#38bdf8"
                label="Cooling"
                value={`${fmtKwh(data.breakdown.cooling_kwh)} kWh`}
                testid="es-breakdown-cooling"
              />
              <BreakdownChip
                icon={<Wind size={14} />}
                color="#84cc16"
                label="Walls"
                value={`${fmtKwh(data.breakdown.walls_kwh)} kWh`}
                testid="es-breakdown-walls"
              />
              <BreakdownChip
                icon={<Wind size={14} />}
                color="#a78bfa"
                label="Windows"
                value={`${fmtKwh(data.breakdown.windows_kwh)} kWh`}
                testid="es-breakdown-windows"
              />
              <BreakdownChip
                icon={<Wind size={14} />}
                color="#f472b6"
                label="Roof"
                value={`${fmtKwh(data.breakdown.roof_kwh)} kWh`}
                testid="es-breakdown-roof"
              />
              <BreakdownChip
                icon={<Sun size={14} />}
                color="#eab308"
                label={`Solar${data.envelope.solar_panels ? ` · ${data.envelope.solar_panels}` : ""}`}
                value={data.breakdown.solar_kwh > 0
                  ? `− ${fmtKwh(data.breakdown.solar_kwh)} kWh`
                  : "none"}
                testid="es-breakdown-solar"
              />
            </div>

            {/* Climate + envelope summary */}
            <div className="grid grid-cols-2 gap-4 border-t border-zinc-800 pt-3 text-xs">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                  // Climate
                </div>
                <div className="text-zinc-200">
                  Region: <span className="font-mono text-brass-300">{data.climate.region}</span>
                </div>
                <div className="text-zinc-500 text-[10px] font-mono mt-1">
                  HDD {data.climate.hdd} · CDD {data.climate.cdd}
                </div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                  // Envelope
                </div>
                <div className="text-zinc-200">
                  Walls{" "}
                  <span className="font-mono text-zinc-400">
                    {data.envelope.wall_area_m2} m²
                  </span>{" "}
                  · Glass{" "}
                  <span className="font-mono text-zinc-400">
                    {data.envelope.window_area_m2} m²
                  </span>
                </div>
                <div className="text-zinc-500 text-[10px] font-mono mt-1">
                  Floor {data.envelope.floor_area_m2} m² · Roof {data.envelope.roof_area_m2} m²
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t border-zinc-800 text-[10px] font-mono text-zinc-500">
              <span>// Manual-J envelope estimate · not a stamped ResCheck.</span>
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-mono text-xs"
                onClick={runScan}
                disabled={loading}
                data-testid="es-rescan-btn"
              >
                <RefreshCw size={14} className={loading ? "animate-spin mr-2" : "mr-2"} />
                {loading ? "Scanning..." : "Re-scan"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BreakdownChip({ icon, color, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-zinc-800 bg-zinc-900/40 px-3 py-2 flex items-start gap-2"
    >
      <div style={{ color }} className="mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">
          {label}
        </div>
        <div className="font-mono text-zinc-100">{value}</div>
      </div>
    </div>
  );
}
